
import React, { useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useFileUpload } from "@/hooks/useFileUpload";
import { Camera, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth"; // Import useAuth

interface AvatarUploadProps {
  currentAvatarUrl?: string;
  fallback: string;
  onAvatarChange: (url: string | null) => void;
  className?: string;
  size?: "sm" | "md" | "lg";
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatarUrl,
  fallback,
  onAvatarChange,
  className,
  size = "md",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  // Get the current user
  const { user } = useAuth();

  const { uploadFile, uploading, progress } = useFileUpload({
    bucket: "avatars",
    allowedTypes: [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ],
    maxSizeBytes: 2 * 1024 * 1024, // 2MB
    onSuccess: (url) => {
      onAvatarChange(url);
      toast({
        description: "Avatar uploaded successfully.",
        variant: "default",
      });
    },
    onError: (msg: string) => {
      toast({
        description: msg,
        variant: "destructive",
      });
    },
  });

  const sizeClasses = {
    sm: "h-16 w-16",
    md: "h-24 w-24",
    lg: "h-32 w-32",
  };

  // Helper: get "safe" file name to avoid collisions
  function getAvatarPath(file: File) {
    if (!user) return null;
    const ext = file.name.split('.').pop() || "jpg";
    // Always overwrite, so use "avatar" + extension
    return `${user.id}/avatar.${ext}`;
  }

  const handleFileSelect = (file: File) => {
    if (!file) return;
    if (!user) {
      toast({ description: "You must be logged in", variant: "destructive" });
      return;
    }
    const path = getAvatarPath(file);
    if (!path) return;
    uploadFile(file, path);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleRemoveAvatar = () => {
    onAvatarChange(null);
    toast({
      description: "Avatar removed.",
      variant: "default",
    });
  };

  // Fallback background: emoji or a colored bg
  const fallbackContent =
    fallback && fallback.length > 0 ? (
      <span>{fallback}</span>
    ) : (
      <span role="img" aria-label="person">
        🌱
      </span>
    );

  return (
    <div className={cn("relative", className)}>
      <div
        className={cn(
          "relative group cursor-pointer rounded-full border-2 border-dashed border-transparent transition-colors",
          dragOver && "border-primary",
          sizeClasses[size]
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <Avatar className={cn("h-full w-full", uploading && "opacity-50")}>
          <AvatarImage src={currentAvatarUrl || ""} alt="Avatar" />
          <AvatarFallback className="text-lg">{fallbackContent}</AvatarFallback>
        </Avatar>

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Camera className="h-6 w-6 text-white" />
        </div>

        {/* Loading overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-black/70 rounded-full flex flex-col items-center justify-center text-white">
            <Upload className="h-4 w-4 mb-2" />
            <div className="text-xs">Uploading...</div>
          </div>
        )}
      </div>

      {/* Progress bar */}
      {uploading && (
        <div className="mt-2">
          <Progress value={progress} className="h-2" />
        </div>
      )}

      {/* Remove button */}
      {currentAvatarUrl && !uploading && (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveAvatar();
          }}
          className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
        >
          <X className="h-3 w-3" />
        </Button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelect(file);
        }}
        className="hidden"
      />
    </div>
  );
};
